import { useMemo } from 'react'
import { useLocation, useNavigate } from '@tanstack/react-router'
import { Link } from '@tanstack/react-router'
import { cn } from '@/lib/utils'
import {
  activeWorkspaceNavigationItem,
  workspaceRelativeHref,
} from '@/lib/workspace-navigation'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'

interface SettingSidebarItem {
  href: string
  title: string
  label: string
  // When true, the item stays active on nested paths (e.g. a detail page
  // under its section) instead of requiring an exact pathname match.
  matchNested?: boolean
  // Optional trailing content — a count badge, or a row action menu.
  badge?: React.ReactNode
  // Search params the link carries, and that must ALL match for the item to
  // read as active. Lets one pathname host a saved view next to the plain page
  // (e.g. "My TODO" = /tests pre-filtered) without both lighting up at once.
  search?: Record<string, string | boolean>
}

interface SettingSidebarProps extends React.HTMLAttributes<HTMLElement> {
  items: SettingSidebarItem[]
}

export default function SettingSidebar({
  className,
  items,
  ...props
}: SettingSidebarProps) {
  const { pathname, search } = useLocation()
  const navigate = useNavigate()

  const fullHrefOf = (item: SettingSidebarItem) =>
    workspaceRelativeHref(pathname, item.href)

  // One item can differ from another only by its search params, so the select's
  // value has to carry them too.
  const keyOf = (item: SettingSidebarItem) =>
    item.search
      ? `${item.href}?${new URLSearchParams(
          Object.entries(item.search).map(([key, value]) => [key, String(value)])
        ).toString()}`
      : item.href

  const activeItem = activeWorkspaceNavigationItem(
    items,
    pathname,
    (search ?? {}) as Record<string, unknown>
  )
  const isItemActive = (item: SettingSidebarItem) => item === activeItem

  const handleSelect = (value: string) => {
    const item = items.find((candidate) => keyOf(candidate) === value)
    if (!item) return
    navigate({
      to: fullHrefOf(item),
      ...(item.search ? { search: item.search } : {}),
    })
  }

  const groups = useMemo(() => items.reduce(
    (acc, item) => {
      const group = item.label
      if (!acc[group]) {
        acc[group] = []
      }
      acc[group].push(item)
      return acc
    },
    {} as Record<string, SettingSidebarItem[]>
  ), [items])

  return (
    <>
      <div className='p-1 md:hidden'>
        <Select
          value={activeItem ? keyOf(activeItem) : undefined}
          onValueChange={handleSelect}
        >
          <SelectTrigger className='h-9 rounded-sm border border-border bg-card font-body text-[12px] font-medium shadow-none sm:w-48'>
            <SelectValue placeholder='Theme' />
          </SelectTrigger>
          <SelectContent className='rounded-sm border border-border bg-card p-1 shadow-none'>
            <span className='px-2 py-1.5 font-body text-[10px] font-semibold uppercase tracking-[0.8px] text-[var(--sidebar-foreground)]/70'>
              {items[0].label}
            </span>
            {items?.map((item) => (
              <SelectItem
                key={keyOf(item)}
                value={keyOf(item)}
                className='rounded-sm font-heading text-[13px] font-medium'
              >
                <div className='flex gap-x-4 px-2 py-1'>
                  <span className='font-heading text-[13px] leading-5 font-medium'>
                    {item.title}
                  </span>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <ScrollArea
        type='hover'
        className='hidden w-full md:block'
      >
        <div className={cn('space-y-1.5', className)} {...props}>
          {Object.entries(groups).map(([group, groupItems], index) => (
            <div key={group}>
              {index > 0 && (
                <div className='!my-3 border-t border-border' />
              )}
              <div className='px-1 pb-1 text-[10px] font-semibold uppercase tracking-[1.2px] text-[#A1A19B]'>
                {group}
              </div>
              <div className='space-y-1.5'>
                {groupItems.map((item) => {
                  const isActive = isItemActive(item)
                  return (
                    <Link
                      preload={false}
                      key={keyOf(item)}
                      to={fullHrefOf(item)}
                      {...(item.search ? { search: item.search } : {})}
                      className={cn(
                        'flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-[13px] leading-[1.2] transition-colors',
                        isActive
                          ? 'bg-muted font-medium text-text-secondary dark:bg-selected'
                          : 'bg-transparent text-text-body-muted hover:bg-muted hover:text-text-secondary dark:text-text-subtle'
                      )}
                    >
                      <span className='truncate'>{item.title}</span>
                      {item.badge}
                    </Link>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>
    </>
  )
}
